import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  Timestamp,
  limit,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { COLLECTIONS } from "@/constants/firestoreCollections";

function normalizeDates(data: Record<string, unknown>): Record<string, unknown> {
  const result = { ...data };
  for (const key of ["createdAt", "updatedAt", "expiresAt"]) {
    const val = result[key];
    if (val && typeof (val as { toDate?: () => Date }).toDate === "function") {
      result[key] = (val as Timestamp).toDate();
    }
  }
  return result;
}

export async function getAllQuotes() {
  const q = query(
    collection(db, COLLECTIONS.QUOTES),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({
    id: d.id,
    ...normalizeDates(d.data()),
  }));
}

export async function getQuoteById(id: string) {
  const ref = doc(db, COLLECTIONS.QUOTES, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...normalizeDates(snap.data()) };
}

export async function getNextQuoteNumber(): Promise<string> {
  const today = new Date();
  const dateStr =
    today.getFullYear().toString() +
    String(today.getMonth() + 1).padStart(2, "0") +
    String(today.getDate()).padStart(2, "0");

  const prefix = `COT-${dateStr}-`;

  const q = query(
    collection(db, COLLECTIONS.QUOTES),
    where("quoteNumber", ">=", prefix),
    where("quoteNumber", "<=", prefix + "\uf8ff"),
    orderBy("quoteNumber", "desc"),
    limit(1)
  );
  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return `${prefix}001`;
  }

  const lastNumber = snapshot.docs[0].data().quoteNumber as string;
  const seq = parseInt(lastNumber.split("-").pop() || "0", 10);
  return `${prefix}${String(seq + 1).padStart(3, "0")}`;
}

export async function createQuote(data: Record<string, unknown>) {
  const quoteNumber = await getNextQuoteNumber();
  const docRef = await addDoc(collection(db, COLLECTIONS.QUOTES), {
    ...data,
    quoteNumber,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { id: docRef.id, quoteNumber };
}

export async function updateQuote(id: string, data: Record<string, unknown>) {
  const ref = doc(db, COLLECTIONS.QUOTES, id);
  await updateDoc(ref, {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteQuote(id: string) {
  await deleteDoc(doc(db, COLLECTIONS.QUOTES, id));
}

export async function updateQuoteStatus(id: string, status: string) {
  const ref = doc(db, COLLECTIONS.QUOTES, id);
  await updateDoc(ref, {
    status,
    updatedAt: serverTimestamp(),
  });
}

// --- Panel optimizado ---

export async function getQuoteCount(): Promise<number> {
  const snapshot = await getCountFromServer(collection(db, COLLECTIONS.QUOTES));
  return snapshot.data().count;
}

export async function getRecentQuotes(max = 5) {
  const q = query(
    collection(db, COLLECTIONS.QUOTES),
    orderBy("createdAt", "desc"),
    limit(max)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...normalizeDates(d.data()) }));
}

export async function getQuotesByMonth(): Promise<{ month: string; count: number }[]> {
  const snapshot = await getDocs(collection(db, COLLECTIONS.QUOTES));
  const counts: Record<string, number> = {};
  snapshot.docs.forEach((d) => {
    const createdAt = d.data().createdAt;
    let date: Date | null = null;
    if (createdAt && typeof (createdAt as Timestamp).toDate === "function") {
      date = (createdAt as Timestamp).toDate();
    } else if (createdAt instanceof Date) {
      date = createdAt;
    }
    if (!date) return;
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    counts[key] = (counts[key] || 0) + 1;
  });
  return Object.entries(counts)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-6)
    .map(([month, count]) => ({ month, count }));
}

